import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  UnifiedTheme,
} from '@backstage/theme';

/**
 * Sistema de Diseño Vanti (Figma, archivo Foundations). Usar estas constantes
 * en vez de escribir hex sueltos.
 */
export const VANTI = {
  navy: '#113455', // Blue King B300: títulos, navegación, botón secundario
  navyDark: '#0C243B', // Blue King B500
  navyDeep: '#081929', // Blue King B600
  navySoft: '#E7EBEE', // Blue King B50
  gold: '#F6CC5C', // Yellow V Y300: botón primario, acentos
  goldHover: '#F3BD2C', // botón primario en hover
  blue: '#4A87C2', // Blue S S400
  blueSoft: '#EEF6FD', // Blue S S50: encabezado de tablas y secciones
  ink: '#091E42', // Gray G900: títulos
  black: '#11171F', // Secondary Black
  bg: '#F5F6F7', // Gray G20: fondo general
  panel: '#FAFBFB', // Gray G10: contenedores agrupadores
  gray: '#505F79', // Gray G400: labels y subtítulos
  border: '#DFE2E6', // Gray G40: líneas de tabla
  outline: '#C2C7D0', // Gray G50: bordes de campos
  disabled: '#EBEDF0', // Gray G30: fondo deshabilitado
  disabledText: '#98A1B0', // Gray G80: texto deshabilitado
  green: '#44AE76',
  orange: '#F29558',
  red: '#DB4B4B',
};

const SOMBRA_MD = '0 2px 4px rgba(16, 24, 40, 0.06), 0 4px 8px rgba(16, 24, 40, 0.10)';
const SOMBRA_LG = '0 4px 6px rgba(16, 24, 40, 0.03), 0 12px 16px rgba(16, 24, 40, 0.08)';

export const FONT_FAMILY =
  '"Mulish", "Muli", "Helvetica Neue", Helvetica, Arial, sans-serif';

// Forma del header del sistema de diseño: navy con la curva dorada en trazo.
const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1368 400' preserveAspectRatio='xMaxYMid slice'>
<path d='M1000 400 C1075 400 1095 350 1110 280 L1150 95 C1165 25 1200 0 1270 0' fill='none' stroke='${VANTI.gold}' stroke-width='3'/>
</svg>`;
const vantiShape = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

const header = genPageTheme({
  colors: [VANTI.navy, VANTI.navy],
  shape: vantiShape,
});

const pageTheme = Object.fromEntries(
  [
    'home',
    'documentation',
    'tool',
    'service',
    'website',
    'library',
    'other',
    'app',
    'apis',
    'card',
    'system',
    'domain',
    'group',
    'user',
  ].map(id => [id, header]),
);

function vantiTheme(variant: 'light' | 'dark'): UnifiedTheme {
  const dark = variant === 'dark';
  return createUnifiedTheme({
    fontFamily: FONT_FAMILY,
    defaultPageTheme: 'home',
    pageTheme,
    palette: {
      ...(dark ? palettes.dark : palettes.light),
      primary: { main: dark ? VANTI.gold : VANTI.navy },
      secondary: { main: VANTI.blue },
      background: dark
        ? { default: VANTI.navyDeep, paper: VANTI.navyDark }
        : { default: VANTI.bg, paper: '#FFFFFF' },
      text: dark
        ? { primary: '#FFFFFF', secondary: VANTI.outline }
        : { primary: VANTI.ink, secondary: VANTI.gray },
      link: dark ? VANTI.gold : VANTI.navy,
      highlight: VANTI.blueSoft,
      status: {
        ok: VANTI.green,
        warning: VANTI.orange,
        error: VANTI.red,
        running: VANTI.blue,
        pending: VANTI.gold,
        aborted: VANTI.gray,
      },
      navigation: {
        background: dark ? VANTI.navyDark : VANTI.navy,
        indicator: VANTI.gold,
        color: 'rgba(255, 255, 255, 0.78)',
        selectedColor: '#FFFFFF',
        navItem: { hoverBackground: 'rgba(255, 255, 255, 0.08)' },
        submenu: { background: VANTI.navyDark },
      },
      tabbar: { indicator: VANTI.gold },
    },
    components: {
      // Escala tipográfica del sistema de diseño
      MuiTypography: {
        styleOverrides: {
          h1: { fontSize: 32, fontWeight: 700, lineHeight: 1.25 },
          h2: { fontSize: 28, fontWeight: 700, lineHeight: 1.2 },
          h3: { fontSize: 24, fontWeight: 700, lineHeight: 1.25 },
          h4: { fontSize: 20, fontWeight: 700, lineHeight: 1.3 },
          h5: { fontSize: 18, fontWeight: 700, lineHeight: 1.3 },
          h6: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 },
          body1: { fontSize: 16, lineHeight: 1.25 },
          body2: { fontSize: 14, lineHeight: 1.25 },
          caption: { fontFamily: '"Inter", sans-serif', fontSize: 12, lineHeight: 1.35 },
        },
      },
      // Esquinas: 16 en cajas, 12 y 8 en las internas, 4 en campos
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
          elevation1: { boxShadow: SOMBRA_MD },
          elevation2: { boxShadow: SOMBRA_MD },
          elevation8: { boxShadow: SOMBRA_LG },
          elevation24: { boxShadow: SOMBRA_LG },
        },
      },
      MuiCard: { styleOverrides: { root: { borderRadius: 16 } } },
      MuiCardHeader: {
        styleOverrides: { title: { fontWeight: 700, fontSize: 20, lineHeight: 1.3 } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 999, fontWeight: 400, fontSize: 14 } },
      },
      // Botones: píldora, amarillo el primario y contorno navy el secundario
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 14,
            lineHeight: 1.25,
            minHeight: 32,
            padding: '6px 16px',
            whiteSpace: 'nowrap',
          },
          sizeLarge: { fontSize: 16, minHeight: 42, padding: '8px 16px' },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&$disabled': { backgroundColor: VANTI.disabled, color: VANTI.disabledText },
          },
          containedPrimary: {
            backgroundColor: VANTI.gold,
            color: '#000000',
            '&:hover': { backgroundColor: VANTI.goldHover },
          },
          outlinedPrimary: dark
            ? {}
            : {
                borderColor: VANTI.navy,
                '&:hover': { backgroundColor: VANTI.navy, color: '#FFFFFF' },
              },
        },
      },
      // Campos: esquina 4, borde G50 y label G400
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 4 },
          notchedOutline: dark ? {} : { borderColor: VANTI.outline },
        },
      },
      // Tablas: encabezado azul claro con título navy y líneas grises, sin cebra
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: dark ? VANTI.navy : VANTI.border },
          head: {
            backgroundColor: `${dark ? VANTI.navy : VANTI.blueSoft} !important`,
            color: `${dark ? '#FFFFFF' : VANTI.navy} !important`,
            fontWeight: 700,
            textTransform: 'none !important' as 'none',
            '& *': { color: `${dark ? '#FFFFFF' : VANTI.navy} !important` },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:nth-of-type(odd)': { backgroundColor: 'transparent' } },
        },
      },
      // La página ocupa lo que deja el menú superior (ALTO_MENU)
      BackstagePage: { styleOverrides: { root: { height: 'calc(100vh - 80px)' } } },
      BackstageHeader: {
        styleOverrides: {
          title: { fontWeight: 700, fontSize: 28 },
          // Más bajo que el de Backstage: va debajo del menú superior
          header: { boxShadow: 'none', padding: '16px 24px' },
        },
      },
    },
  });
}

export const vantiLightTheme = vantiTheme('light');
export const vantiDarkTheme = vantiTheme('dark');
